import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import {
  Mic, Square, Loader2, Volume2, Send, Briefcase, Presentation, Languages,
  Sparkles, RotateCcw,
} from "lucide-react";

const SCENARIOS = [
  { id: "interview", label: "Interview Prep", desc: "Mock interviews + feedback", icon: Briefcase },
  { id: "public_speaking", label: "Public Speaking", desc: "Practice pitches & speeches", icon: Presentation },
  { id: "language", label: "Language Practice", desc: "Conversation + corrections", icon: Languages },
  { id: "general", label: "Life Coaching", desc: "Hands-free coaching chat", icon: Sparkles },
];

export default function VoiceCoach() {
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [text, setText] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages, processing]);
  useEffect(() => () => streamRef.current?.getTracks().forEach((t) => t.stop()), []);

  const pickScenario = (id) => { setScenario(id); setMessages([]); setSessionId(null); };

  const playAudio = (b64) => {
    try { new Audio(`data:audio/mp3;base64,${b64}`).play().catch(() => {}); } catch { /* ignore */ }
  };

  const handleResponse = (data) => {
    setSessionId(data.session_id);
    setMessages((m) => [...m,
      { role: "user", content: data.transcript },
      { role: "assistant", content: data.reply, audio: data.audio_base64 }]);
    playAudio(data.audio_base64);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const types = ["audio/webm", "audio/mp4", "audio/ogg", "audio/wav"];
      const mime = types.find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => sendAudio(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }));
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access is needed. Please allow it and try again.");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const sendAudio = async (blob) => {
    setProcessing(true);
    const fd = new FormData();
    fd.append("audio", blob, "recording.webm");
    fd.append("scenario", scenario);
    if (sessionId) fd.append("session_id", sessionId);
    try {
      const { data } = await api.post("/voice/coach", fd);
      handleResponse(data);
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  const sendText = async () => {
    if (!text.trim()) return;
    const msg = text.trim();
    setText("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setProcessing(true);
    try {
      const { data } = await api.post("/voice/coach/text", { message: msg, scenario, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, audio: data.audio_base64 }]);
      playAudio(data.audio_base64);
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  // ----- Scenario picker -----
  if (!scenario) return (
    <div className="font-quicksand" data-testid="voice-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <p className="flex items-center gap-2 font-quicksand text-sm font-semibold text-[#FF7E67]"><Mic className="h-4 w-4" strokeWidth={2.5} /> Voice Coach</p>
        <h1 className="mt-1 font-nunito text-4xl font-extrabold tracking-tight text-[#2D3748] sm:text-5xl">Talk it out loud</h1>
        <p className="mt-2 font-quicksand text-base font-medium text-[#718096]">Pick a mode and practice by speaking — your coach listens and talks back.</p>
      </motion.div>
      <div className="grid gap-6 sm:grid-cols-2">
        {SCENARIOS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.button key={s.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => pickScenario(s.id)} data-testid={`scenario-${s.id}`}
              className="group flex items-center gap-4 rounded-[2rem] border border-[#FFE8E3] bg-white p-6 text-left shadow-warm transition-transform duration-200 hover:-translate-y-1">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4E6] transition-colors group-hover:bg-[#FFE8E3]">
                <Icon className="h-7 w-7 text-[#FF7E67]" strokeWidth={2.5} />
              </span>
              <div>
                <p className="font-nunito text-lg font-bold text-[#2D3748]">{s.label}</p>
                <p className="font-quicksand text-sm text-[#718096]">{s.desc}</p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  const current = SCENARIOS.find((s) => s.id === scenario);

  // ----- Conversation -----
  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col font-quicksand" data-testid="voice-session">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFF4E6]"><current.icon className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /></span>
          <div>
            <h1 className="font-nunito text-xl font-extrabold text-[#2D3748]">{current.label}</h1>
            <p className="font-quicksand text-xs text-[#718096]">Speak or type — your coach replies out loud</p>
          </div>
        </div>
        <button onClick={() => setScenario(null)} data-testid="reset-session-btn"
          className="flex items-center gap-2 rounded-full border border-[#FFE8E3] px-4 py-2 font-nunito text-sm font-bold text-[#718096] transition-colors hover:border-[#FF7E67] hover:text-[#FF7E67]">
          <RotateCcw className="h-4 w-4" strokeWidth={2.5} /> New session
        </button>
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto rounded-[2rem] border border-[#FFE8E3] bg-white p-6 shadow-warm" data-testid="voice-conversation">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF4E6]"><Mic className="h-8 w-8 text-[#FFB347]" strokeWidth={2} /></span>
            <p className="mt-4 font-nunito text-lg font-bold text-[#2D3748]">Tap the mic to start</p>
            <p className="font-quicksand text-sm text-[#718096]">Say hello, ask a question, or start practicing.</p>
          </div>
        )}
        <AnimatePresence>
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 font-quicksand text-base ${m.role === "user" ? "bg-sunrise text-white" : "bg-[#FFF9F5] text-[#2D3748]"}`} data-testid={`voice-msg-${m.role}`}>
                {m.content}
                {m.role === "assistant" && m.audio && (
                  <button onClick={() => playAudio(m.audio)} data-testid="replay-audio-btn"
                    className="mt-2 flex items-center gap-1 font-nunito text-xs font-bold text-[#FF7E67]"><Volume2 className="h-4 w-4" strokeWidth={2.5} /> Replay</button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {processing && <div className="flex items-center gap-2 text-[#718096]"><Loader2 className="h-4 w-4 animate-spin" /> <span className="font-quicksand text-sm">Coach is thinking…</span></div>}
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-[#FFE8E3] bg-white px-4 py-2 shadow-warm">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
            placeholder="Or type your message…" data-testid="voice-text-input"
            className="flex-1 bg-transparent font-quicksand text-[#2D3748] outline-none placeholder:text-[#A0AEC0]" />
          <button onClick={sendText} disabled={!text.trim() || processing} data-testid="voice-text-send"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFE8E3] text-[#FF7E67] disabled:opacity-50"><Send className="h-4 w-4" strokeWidth={2.5} /></button>
        </div>
        <button onClick={recording ? stopRecording : startRecording} disabled={processing} data-testid="mic-btn"
          className={`relative flex h-14 w-14 items-center justify-center rounded-full text-white shadow-glow-coral transition-transform duration-200 hover:scale-105 disabled:opacity-60 ${recording ? "bg-[#E53E3E]" : "bg-sunrise"}`}>
          {recording && <span className="absolute inset-0 animate-ping rounded-full bg-[#E53E3E] opacity-40" />}
          {recording ? <Square className="h-5 w-5" strokeWidth={2.5} /> : <Mic className="h-6 w-6" strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
}
